import BasePlayer from './BasePlayer';
import { getNextPlayRequestId } from '../common/channelPlayerState';
import { createHttpStreamCompletedHandler, markChannelStreamReady } from '../common/channelStreamUrl';

/**
 * Samsung Tizen (legacy) player implementation
 * Uses window.webapis.avplay API
 */
class SamsungPlayer extends BasePlayer {
  constructor() {
    super();
    this.playerCoords = null;
  }

  supportsFullscreenToggle() {
    return true;
  }

  async play(url, isFullScreen = false) {
    const playerState = this.getPlayerState();
    const myRequestId = getNextPlayRequestId();
    this.updatePlayerState({ url });

    // Stop and hide existing player if any
    if (playerState.gAVplayObj) {
      try {
        playerState.gAVplayObj.stop();
        playerState.gAVplayObj.hide();
      } catch (e) {
        console.warn('Error stopping previous Samsung player:', e);
      }
    }

    return new Promise((resolve, reject) => {
      window.webapis.avplay.getAVPlay(
        (avplayObj) => this._handlePlaySuccess(avplayObj, isFullScreen, myRequestId, resolve, reject),
        (error) => this._handlePlayError(error, reject)
      );
    });
  }

  _handlePlaySuccess(avplayObj, isFullScreen, myRequestId, resolve, reject) {
    const playerState = this.getPlayerState();
    const self = this;
    
    this.playerCoords = {
      top: !isFullScreen ? 150 : 0,
      left: !isFullScreen ? Math.round(window.innerWidth / 3) : 0,
      width: !isFullScreen ? 600 : window.innerWidth,
      height: !isFullScreen ? 400 : window.innerHeight,
    };

    this.updatePlayerState({ gAVplayObj: avplayObj, playerCoords: this.playerCoords });

    avplayObj.init({
      containerID: 'player_container',
      zIndex: 1,
      bufferingCallback: {
        onbufferingstart: () => console.log('Samsung: buffering started'),
        onbufferingprogress: (percent) => console.log('Samsung: buffering progress:', percent),
        onbufferingcomplete: () => {
          console.log('Samsung: buffering complete');
          markChannelStreamReady(myRequestId);
        },
      },
      playCallback: {
        oncurrentplaytime: (time) => {
          console.log('Samsung: playing time:', time);
          if (time > 0 && myRequestId === self.getPlayerState().playRequestId) {
            self.updatePlayerState({ httpPlayTimeMs: time, httpLastPlayAt: Date.now() });
            markChannelStreamReady(myRequestId);
          }
        },
        onresolutionchanged: (width, height) => console.log('Samsung: resolution changed:', width, height),
        onstreamcompleted: createHttpStreamCompletedHandler(self, myRequestId),
        onerror: (error) => console.error('Samsung: playback error:', error.name),
      },
      displayRect: this.playerCoords,
      autoratio: false,
    });

    avplayObj.open(playerState.url);
    avplayObj.play(
      () => {
        console.log('Samsung: playback started successfully');
        resolve();
      },
      (error) => {
        console.error('Samsung: play failed:', error.message);
        reject(error || new Error('Samsung play failed'));
      }
    );
  }

  _handlePlayError(error, reject) {
    console.error('Samsung: cannot get avplay object:', error.name);
    reject(error);
  }

  async stop() {
    const playerState = this.getPlayerState();
    if (playerState.gAVplayObj) {
      try {
        playerState.gAVplayObj.stop();
        playerState.gAVplayObj.close();
        console.log('Samsung: player stopped');
      } catch (e) {
        console.warn('Samsung: error stopping player:', e);
      }
    }
  }

  async setDisplayRect(coords) {
    const playerState = this.getPlayerState();
    if (playerState.gAVplayObj) {
      const displayCoords = {
        top: coords.top ?? coords.y ?? 0,
        left: coords.left ?? coords.x ?? 0,
        width: coords.width,
        height: coords.height,
      };
      this.playerCoords = displayCoords;
      this.updatePlayerState({ playerCoords: displayCoords });
      playerState.gAVplayObj.setDisplayRect(displayCoords);
      console.log('Samsung: display rect set:', displayCoords);
    }
  }

  async toggleFullscreen() {
    const playerState = this.getPlayerState();
    const newFullScreenState = !playerState.displayFullScreen;
    this.updatePlayerState({ displayFullScreen: newFullScreenState });

    const coords = newFullScreenState
      ? { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }
      : { top: 150, left: Math.round(window.innerWidth / 3), width: 600, height: 400 };

    await this.setDisplayRect(coords);
  }
}

export default SamsungPlayer;

